// src/pages/ReportsPage.tsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    collection, doc, getDoc, getDocs
} from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { db } from "../firebase";
import { computeBillingSteps } from "../lib/billing";
import { normalizeSummaryCounts } from "../lib/billing";
import type { InvoiceSummary } from "../lib/billing";
import { REPORTS_COLLECTION, INVOICE_SUBCOLLECTION, FN_DELETE_INVOICE_REPORT } from "../lib/reports";
import { AppShellLayout } from "../components/AppShellLayout";

// Item (förenklad: id + övriga fält som any)
type Item = { id: string } & Record<string, any>;

// Rapport-dokument
type InvoiceReport = {
    name: string;
    customer: string;
    createdAt: string;
    createdBy: string | null;
    itemIds: string[];
    summary: InvoiceSummary;
};

function fmtDateOnly(d: any): string {
    if (!d) return "—";
    const date: Date =
        typeof d?.toDate === "function" ? d.toDate() :
            typeof d === "string" ? new Date(d) :
                d instanceof Date ? d : new Date(NaN);
    return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("sv-SE");
}

export default function ReportsPage() {
    const functions = getFunctions(undefined, "europe-west1");

    const [invoiceReports, setInvoiceReports] = useState<Array<{ id: string } & InvoiceReport>>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [reportItems, setReportItems] = useState<Record<string, Item[]>>({});
    const [loadingDetail, setLoadingDetail] = useState<string | null>(null);

    // Delete-modal
    const [showDeleteReportModal, setShowDeleteReportModal] = useState(false);
    const [reportDeleteConfirmText, setReportDeleteConfirmText] = useState("");
    const [pendingReportToDelete, setPendingReportToDelete] = useState<(InvoiceReport & { id: string }) | null>(null);

    function openDeleteReportModal(report: InvoiceReport & { id: string }) {
        setPendingReportToDelete(report);
        setReportDeleteConfirmText("");
        setShowDeleteReportModal(true);
    }
    function cancelDeleteReportModal() {
        setShowDeleteReportModal(false);
        setPendingReportToDelete(null);
        setReportDeleteConfirmText("");
    }
    async function confirmDeleteReportModal() {
        if (!pendingReportToDelete) return;
        if (reportDeleteConfirmText !== "DELETE") return;

        try {
            const fn = httpsCallable<any, any>(functions, FN_DELETE_INVOICE_REPORT);
            await fn({ reportId: pendingReportToDelete.id });

            setInvoiceReports(prev => prev.filter(x => x.id !== pendingReportToDelete.id));
            setExpandedId(null);

            const w = window as any;
            if (typeof w.gwFetchFirstPage === "function") {
                await w.gwFetchFirstPage();
            }
        } catch (err) {
            console.error("Kunde inte ta bort rapport:", err);
            alert("Kunde inte ta bort rapporten.");
        } finally {
            cancelDeleteReportModal();
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const colRef = collection(doc(db, REPORTS_COLLECTION, "root"), INVOICE_SUBCOLLECTION);
                const snap = await getDocs(colRef);

                const docs = snap.docs.map((d) => {
                    const raw = d.data() as any;
                    const createdAt =
                        raw.createdAt?.toDate?.() instanceof Date
                            ? (raw.createdAt.toDate() as Date).toISOString()
                            : typeof raw.createdAt === "string"
                                ? raw.createdAt
                                : "";
                    const deletedAt = typeof raw.deletedAt === "undefined" ? null : raw.deletedAt;
                    return {
                        id: d.id,
                        ...raw,
                        createdAt,
                        deletedAt,
                    } as unknown as InvoiceReport & { id: string; deletedAt: any };
                });

                docs.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
                setInvoiceReports(docs.filter((r: any) => r.deletedAt === null));
            } catch (e) {
                console.error("Kunde inte hämta rapporter:", e);
                setInvoiceReports([]);
            }
        })();
    }, []);

    const loadReportItems = async (r: { id: string } & InvoiceReport) => {
        if (reportItems[r.id]) return;
        setLoadingDetail(r.id);
        try {
            const items: Item[] = [];
            for (const id of r.itemIds) {
                try {
                    const s = await getDoc(doc(db, "itInventory", id));
                    if (s.exists()) items.push({ id: s.id, ...(s.data() as any) } as Item);
                } catch (e) {
                    console.warn("Kunde inte läsa item", id, e);
                }
            }
            setReportItems((prev) => ({ ...prev, [r.id]: items }));
        } finally {
            setLoadingDetail(null);
        }
    };

    return (
        <AppShellLayout>
            <h1 className="gw-h1">Rapporter</h1>

            {invoiceReports.length === 0 ? (
                <div className="text-muted">Inga fakturarapporter skapade ännu.</div>
            ) : (
                <div className="grid gap-4">
                    {invoiceReports.map((r) => {
                        const isOpen = expandedId === r.id;

                        const s: any = (r as any).summary ?? {};
                        const { totalItems, reusedCount, resoldCount, scrappedCount } = normalizeSummaryCounts(s);
                        const totalAmount = (s.totalAmount ?? s.total);

                        const fmtSEK = (v: unknown) =>
                            typeof v === "number"
                                ? new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(v)
                                : null;

                        return (
                            <div key={r.id} className="gw-card">
                                <div className="p-4">
                                    <div
                                        onClick={async () => {
                                            const next = isOpen ? null : r.id;
                                            setExpandedId(next);
                                            if (next) await loadReportItems(r);
                                        }}
                                        className="cursor-pointer"
                                        title={isOpen ? "Klicka för att stänga" : "Klicka för att visa detaljer"}
                                    >
                                        <h3 className="gw-h3 m-0">
                                            <strong>{r.name}</strong> — {r.customer}
                                        </h3>
                                        <div className="text-sm text-muted mt-1">{new Date(r.createdAt).toLocaleString("sv-SE")}</div>
                                    </div>

                                    <div className="text-sm mt-2">
                                        <a
                                            href={`${window.location.origin}/#/rapport/${encodeURIComponent(r.id)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-primary hover:underline"
                                        >
                                            Öppna i nytt fönster
                                        </a>
                                    </div>

                                    <div className="text-sm text-text mt-3 flex flex-wrap gap-x-3 gap-y-1">
                                        {totalAmount != null && <span className="font-semibold">{fmtSEK(totalAmount)}</span>}
                                        <span>• {totalItems} enheter</span>
                                        <span>• Återbruk: {reusedCount}</span>
                                        <span>• Vidaresålt: {resoldCount}</span>
                                        <span>• Skrotat: {scrappedCount}</span>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="border-t border-border p-4">
                                        {!reportItems[r.id] ? (
                                            <div className="text-muted">
                                                {loadingDetail === r.id ? "Laddar…" : "Ingen data."}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="text-xs text-muted mb-3">
                                                    {(() => {
                                                        const t = computeBillingSteps(reportItems[r.id]!);
                                                        return (
                                                            <>
                                                                <strong>Summering:</strong>{" "}
                                                                F3: {t.f3Procedure} • EP-rem: {t.endpointRemoval} •{" "}
                                                                OS: {t.osReinstall} • EP-wipe: {t.endpointWipe} •{" "}
                                                                Boot-test: {t.postWipeBootTest} • Radering: {t.dataErasure} •{" "}
                                                                Refurb: {t.refurbish}
                                                            </>
                                                        );
                                                    })()}
                                                </div>

                                                <div className="gw-table-wrap">
                                                    <table className="gw-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Ordernr</th>
                                                                <th>Tillverkare</th>
                                                                <th>Modell</th>
                                                                <th>Serienr</th>
                                                                <th>Kund</th>
                                                                <th>Klart av</th>
                                                                <th>Datum</th>
                                                                <th>Status</th>
                                                                <th className="w-1 whitespace-nowrap">F3-procedur</th>
                                                                <th className="w-1 whitespace-nowrap">Borttagning i Endpoint</th>
                                                                <th className="w-1 whitespace-nowrap">Ominstallation OS</th>
                                                                <th className="w-1 whitespace-nowrap">Wipe i Endpoint</th>
                                                                <th className="w-1 whitespace-nowrap">Uppstartstest efter Wipe</th>
                                                                <th className="w-1 whitespace-nowrap">Dataradering</th>
                                                                <th className="w-1 whitespace-nowrap">Refurbish</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {reportItems[r.id]!
                                                                .slice()
                                                                .sort((a, b) => String(b.completedAt || "").localeCompare(String(a.completedAt || "")))
                                                                .map((it) => {
                                                                    const statusParts: string[] = [];
                                                                    if ((it as any).reuse) statusParts.push("Återbruk");
                                                                    if ((it as any).resold) statusParts.push("Vidaresålt");
                                                                    if ((it as any).scrap) statusParts.push("Skrotad");
                                                                    const status = statusParts.join(" / ") || "-";

                                                                    const hasSteps = typeof (it as any).f3Procedure === "number";
                                                                    const steps = hasSteps
                                                                        ? {
                                                                            f3Procedure: (it as any).f3Procedure ?? 0,
                                                                            endpointRemoval: (it as any).endpointRemoval ?? 0,
                                                                            osReinstall: (it as any).osReinstall ?? 0,
                                                                            endpointWipe: (it as any).endpointWipe ?? 0,
                                                                            postWipeBootTest: (it as any).postWipeBootTest ?? 0,
                                                                            dataErasure: (it as any).dataErasure ?? 0,
                                                                            refurbish: (it as any).refurbish ?? 0,
                                                                        }
                                                                        : computeBillingSteps({
                                                                            reuse: !!(it as any).reuse,
                                                                            resold: !!(it as any).resold,
                                                                            scrap: !!(it as any).scrap,
                                                                        });

                                                                    return (
                                                                        <tr key={it.id}>
                                                                            <td>{it.orderNumber}</td>
                                                                            <td>{it.manufacturer}</td>
                                                                            <td>{it.model}</td>
                                                                            <td>{it.serial}</td>
                                                                            <td>{(it as any).customer}</td>
                                                                            <td>{it.completedBy}</td>
                                                                            <td>{fmtDateOnly(it.completedAt)}</td>
                                                                            <td>{status}</td>
                                                                            <td className="text-center">{steps.f3Procedure}</td>
                                                                            <td className="text-center">{steps.endpointRemoval}</td>
                                                                            <td className="text-center">{steps.osReinstall}</td>
                                                                            <td className="text-center">{steps.endpointWipe}</td>
                                                                            <td className="text-center">{steps.postWipeBootTest}</td>
                                                                            <td className="text-center">{steps.dataErasure}</td>
                                                                            <td className="text-center">{steps.refurbish}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div className="mt-4">
                                                    <button
                                                        type="button"
                                                        className="gw-btn gw-btn-danger gw-btn-sm"
                                                        onClick={() => openDeleteReportModal(r)}
                                                        title="Ta bort hela rapporten"
                                                    >
                                                        Ta bort rapport
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

            )}

            {showDeleteReportModal && createPortal(
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-report-title"
                    className="gw-modal-overlay"
                    onClick={cancelDeleteReportModal}
                >
                    <div
                        className="gw-modal-card gw-modal-card--narrow"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="delete-report-title" className="gw-modal-title">
                            Bekräfta radering av rapport
                        </h3>

                        <p>
                            Du är på väg att radera rapporten <b>{pendingReportToDelete?.name}</b>{" "}
                            ({pendingReportToDelete?.itemIds?.length || 0} enhet(er)).
                        </p>
                        <p>
                            Enheterna återgår till <b>Fakturering</b> och förblir färdigmarkerade.
                        </p>
                        <p>Skriv <code>DELETE</code> för att bekräfta.</p>

                        <input
                            type="text"
                            autoFocus
                            value={reportDeleteConfirmText}
                            onChange={(e) => setReportDeleteConfirmText(e.target.value)}
                            placeholder='Skriv "DELETE"'
                            aria-label='Skriv "DELETE" för att bekräfta'
                            className="gw-input"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && reportDeleteConfirmText === "DELETE") {
                                    void confirmDeleteReportModal();
                                }
                            }}
                        />

                        <div className="gw-modal-actions">
                            <button onClick={cancelDeleteReportModal} className="gw-btn gw-btn-secondary">
                                Avbryt
                            </button>
                            <button
                                onClick={confirmDeleteReportModal}
                                disabled={reportDeleteConfirmText !== "DELETE"}
                                className="gw-btn gw-btn-danger"
                            >
                                Ja, radera
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </AppShellLayout>
    );
}

